import React, { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { CONTACT_BATCH_MODE } from '../../constants';
import { batchUpdateActions } from '../../behavior/batchUpdate';
import { personsActions } from '../../behavior/persons';
import CommonTagsInputWrapper from '../common/CommonTagsInputWrapper';
import ReplacePicker from './ReplacePicker';

const MultiPersonPicker = ({ formikProps, mode }) => {
  const { t } = useTranslation();
  const list = formikProps.values.contacts || [];
  const [clearCounter, setClearCounter] = useState(0);

  const handlePick = (_name, person) => {
    if (!person || !person.id) return;
    const alreadyThere = list.some((c) => c && c.person && c.person.id === person.id);
    if (!alreadyThere) {
      formikProps.setFieldValue('contacts', [...list, { person }]);
    }
    setClearCounter((n) => n + 1);
  };

  const handleRemove = (id) => {
    formikProps.setFieldValue(
      'contacts',
      list.filter((c) => !(c && c.person && c.person.id === id))
    );
  };

  const flagNotAtCern =
    mode === CONTACT_BATCH_MODE.ADD || mode === CONTACT_BATCH_MODE.OVERRIDE;
  const hasNotAtCern =
    flagNotAtCern && list.some((c) => c && c.person && c.person.atCern === 'N');

  return (
    <Box>
      <CommonTagsInputWrapper
        name="__contactsPicker"
        setFieldValue={handlePick}
        dynamicSuggestionListAction={(v) =>
          personsActions.getPersonAutocompleteListAction(v, false, true, true)
        }
        matchProperty="name"
        displayProps="name"
        value={null}
        maxTags={1}
        onlyUnique={true}
        clearInputCounter={clearCounter}
      />
      {list.length > 0 && (
        <Stack direction="row" flexWrap="wrap" sx={{ mt: 1, gap: 1 }}>
          {list.map((c) => {
            const notAtCern = c.person.atCern === 'N';
            const flagged = flagNotAtCern && notAtCern;
            const chip = (
              <Chip
                key={c.person.id}
                label={c.person.name}
                onDelete={() => handleRemove(c.person.id)}
                color={flagged ? 'error' : 'default'}
                variant={flagged ? 'filled' : 'outlined'}
                icon={flagged ? <WarningAmberIcon /> : undefined}
              />
            );
            return flagged ? (
              <Tooltip key={c.person.id} title={t('batch.update.contacts.forward.not.at.cern.tooltip')}>
                {chip}
              </Tooltip>
            ) : (
              chip
            );
          })}
        </Stack>
      )}
      {hasNotAtCern && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t('batch.update.contacts.forward.not.at.cern.error')}
        </Alert>
      )}
    </Box>
  );
};

const ContactsBatchPanel = (formikProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mode = useSelector((s) => s.batchUpdate.contactBatchMode || CONTACT_BATCH_MODE.ADD);

  const setMode = (newMode) => {
    if (!newMode) return;
    dispatch(batchUpdateActions.setContactBatchMode(newMode));
    formikProps.setFieldValue('contacts', []);
    if (newMode !== CONTACT_BATCH_MODE.REPLACE) {
      formikProps.setFieldValue('replaceFromPerson', null);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('inventory.details.page.contact.list.container.title')}
      </Typography>

      <FormControl size="small" sx={{ mb: 1, minWidth: 220 }}>
        <InputLabel id="contact-batch-mode-label">
          {t('batch.update.contacts.mode.label')}
        </InputLabel>
        <Select
          labelId="contact-batch-mode-label"
          label={t('batch.update.contacts.mode.label')}
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          inputProps={{ 'data-testid': 'contact-batch-mode-select' }}
        >
          <MenuItem value={CONTACT_BATCH_MODE.ADD} data-testid="contact-batch-mode-ADD">
            {t('batch.update.contacts.mode.add')}
          </MenuItem>
          <MenuItem value={CONTACT_BATCH_MODE.REMOVE} data-testid="contact-batch-mode-REMOVE">
            {t('batch.update.contacts.mode.remove')}
          </MenuItem>
          <MenuItem value={CONTACT_BATCH_MODE.REPLACE} data-testid="contact-batch-mode-REPLACE">
            {t('batch.update.contacts.mode.replace')}
          </MenuItem>
          <MenuItem value={CONTACT_BATCH_MODE.OVERRIDE} data-testid="contact-batch-mode-OVERRIDE">
            {t('batch.update.contacts.mode.override')}
          </MenuItem>
        </Select>
      </FormControl>

      <Typography
        variant="body2"
        sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}
        data-testid="contact-batch-mode-help"
      >
        {t(`batch.update.contacts.mode.${mode.toLowerCase()}.help`)}
      </Typography>

      {mode === CONTACT_BATCH_MODE.REPLACE ? (
        <ReplacePicker formikProps={formikProps} />
      ) : (
        <MultiPersonPicker formikProps={formikProps} mode={mode} />
      )}
    </Box>
  );
};

export default ContactsBatchPanel;
